// @flow

import Vue from 'vue'

import type { UserApiClientInterface } from './user-api-client-interface'
import type { User } from '../types/user'
import { LoginResult } from '../types/login-result'
import type { ValidationResult } from '../types/validation-result'
import type { Address } from '../types/address'
import type { CustomerDetails } from '../types/customer-details'
import type { $AxiosError } from 'axios'
import AuthenticationError from './error/authentication-error'
import ValidationError from '../order-api/error/validation-error'
import type { AccountCredentials } from '../types/account-credentials'
import TimeoutError from '../order-api/error/timeout-error'
import ApiError from '../order-api/error/api-error'
import SessionIdStorageInterface from '../service/session-id-storage-interface'
import type {CustomerCards} from '../types/customer-cards'
import type { TokenProviderInterface } from '../token/token-provider-interface'

class UserApiClient implements UserApiClientInterface {
  baseUrl: string
  sessionIdStorage: SessionIdStorageInterface
  tokenProvider: TokenProviderInterface

  constructor (baseUrl: string, sessionIdStorage: SessionIdStorageInterface, tokenProvider: TokenProviderInterface) {
    this.baseUrl = baseUrl
    this.sessionIdStorage = sessionIdStorage
    this.tokenProvider = tokenProvider
  }

  getClientOptions () {
    const headers = {}
    const token = this.tokenProvider.getToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    const options = {
      headers,
      withCredentials: true,
      timeout: 10000
    }
    return options
  }

  getLoginClientOptions () {
    const headers = {}
    const options = {
      headers,
      withCredentials: true,
      timeout: 10000
    }
    return options
  }

  async login (username: string, password: string, type: string): Promise<LoginResult> {
    try {
      return await Vue.$http.post(this.baseUrl + '/login', {username, password, type}, this.getLoginClientOptions())
    } catch (error) {
      if (error.response && error.response.data && error.response.data.code) {
        throw new AuthenticationError(
          error.response.data.code,
          error.response,
          error.response.data.message || 'authentication error'
        )
      }
      throw error
    }
  }

  async getUser (): User {
    try {
      const result: { data: { user: Object } } = await Vue.$http.get(this.baseUrl + '/info', {}, this.getClientOptions())
      return result.data
    } catch (error) {
      throw this.constructor.convertError(error)
    }
  }

  async getJwtUser (token: string): User {
    const result: { data: { user: Object } } = await Vue.$http.get(
      this.baseUrl + '/info',
      { headers: { Authorization: `Bearer ${token}`, 'X-SID': this.sessionIdStorage.getSessionId() } }
    )

    return result.data
  }

  async updateUser (user: User): Promise<ValidationResult> {
    try {
      return await Vue.$http.post(this.baseUrl + '/update', user, this.getClientOptions())
    } catch (error) {
      throw this.constructor.convertError(error)
    }
  }

  static convertError (error: $AxiosError<Object>): Error {
    if (error.response && error.response.data && error.response.data.errors) {
      return new ValidationError(error.response.data.errors, error)
    }
    if (error.code === 'ECONNABORTED') {
      return new TimeoutError(error)
    }
    return new ApiError(error)
  }

  async register (
    customerDetails: CustomerDetails, address: Address,
    credentials: AccountCredentials
  ): Promise<ValidationResult> {
    try {
      const result = await Vue.$http.post(this.baseUrl + '/register', {
        customerDetails: customerDetails,
        address: {
          postcode: address.postalCode,
          houseNumber: address.houseNumber,
          street: address.street,
          city: address.city,
          countryCode: address.countryCode
        },
        account: {
          emailAddress: credentials.emailAddress,
          password: credentials.password
        }
      }, this.getClientOptions())
      return result.data
    } catch (error) {
      throw this.constructor.convertError(error)
    }
  }

  async validateRegistration (
    customerDetails: CustomerDetails, address: Address,
    credentials: { emailAddress: string, password: string }
  ): Promise<ValidationResult> {
    try {
      const result = await Vue.$http.post(this.baseUrl + '/register/validate', {
        customerDetails: customerDetails,
        address: {
          postcode: address.postalCode,
          houseNumber: address.houseNumber,
          street: address.street,
          city: address.city,
          countryCode: address.countryCode
        },
        account: {
          emailAddress: credentials.emailAddress,
          password: credentials.password
        }
      }, this.getClientOptions())
      return result.data
    } catch (error) {
      throw this.constructor.convertError(error)
    }
  }

  async requestPassword (emailAddress: string): Promise<void> {
    try {
      await Vue.$http.post(this.baseUrl + '/request_password', {
        emailAddress: emailAddress
      }, this.getClientOptions())
    } catch (error) {
      throw this.constructor.convertError(error)
    }
  }

  async validateChangePasswordToken (token: string): Promise<void> {
    let result
    try {
      result = await Vue.$http.post(this.baseUrl + '/validate_change_password_token', {
        token: encodeURIComponent(token)
      }, this.getClientOptions())
    } catch (error) {
      throw this.constructor.convertError(error)
    }
    if (result && result.data && result.data.errors) {
      throw new ValidationError(result.data.errors, new Error())
    }
  }

  async changePassword (token: string, newPassword: string): Promise<void> {
    let result
    try {
      result = await Vue.$http.post(this.baseUrl + '/change_password', {
        token: encodeURIComponent(token),
        newPassword: newPassword
      }, this.getClientOptions())
    } catch (error) {
      throw this.constructor.convertError(error)
    }
    if (result && result.data && result.data.errors) {
      throw new ValidationError(result.data.errors, new Error())
    }
  }

  async getCustomerCards (): Promise<CustomerCards> {
    try {
      const result: { data: { engravedIdStrings: Object } } = await Vue.$http.get(
        this.baseUrl + '/cards',
        {},
        this.getClientOptions()
      )
      return result.data
    } catch (error) {
      throw this.constructor.convertError(error)
    }
  }
}

export default UserApiClient



// WEBPACK FOOTER //
// ./src/user-api/user-api-client.js